import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import NewsFeed from '../components/NewsFeed';
import { IMPORTANCE_OPTIONS, CATEGORY_OPTIONS, SOURCE_OPTIONS } from '../utils/newsFeedFilters';
import { useBodyScrollLock } from '../utils/useBodyScrollLock';
import '../styles/NewsFeedPage.css';

const DEFAULT_FILTERS = { importance: [], category: [], source: [] };

const NewsFeedPage = ({ apiBaseUrl }) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [dateRange, setDateRange] = useState({ start_date: '', end_date: '' });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [periodLinks, setPeriodLinks] = useState({ weekly: null, monthly: null });
  useBodyScrollLock(isSidebarOpen);
  const debounceRef = useRef(null);

  useEffect(() => {
    // Exact update_type match (not the 'source' prefix filter) so the _explicit variants never
    // surface here, even for viewers who could otherwise see them.
    Promise.all([
      fetch(`${apiBaseUrl}/newsfeed?update_type=weekly_summary&per_page=1`, { credentials: 'include' }).then((res) => res.json()),
      fetch(`${apiBaseUrl}/newsfeed?update_type=monthly_summary&per_page=1`, { credentials: 'include' }).then((res) => res.json()),
    ])
      .then(([weekly, monthly]) => {
        setPeriodLinks({
          weekly: weekly.updates?.[0]?.id ?? null,
          monthly: monthly.updates?.[0]?.id ?? null,
        });
      })
      .catch((err) => console.error('Failed to load period summary links:', err));
  }, [apiBaseUrl]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const handleFilterChange = (type, value, checked) => {
    setFilters(prev => {
      const next = { ...prev };
      const current = next[type] || [];
      next[type] = checked ? [...current, value] : current.filter(v => v !== value);
      return next;
    });
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setSearch('');
    setFilters(DEFAULT_FILTERS);
    setDateRange({ start_date: '', end_date: '' });
  };

  const activeFilterCount =
    filters.importance.length + filters.category.length + filters.source.length +
    (dateRange.start_date ? 1 : 0) + (dateRange.end_date ? 1 : 0) + (debouncedSearch ? 1 : 0);

  const feedFilters = useMemo(() => {
    const f = {};
    if (debouncedSearch) f.q = debouncedSearch;
    if (filters.importance.length) f.importance = filters.importance.join(',');
    if (filters.category.length) f.category = filters.category.join(',');
    if (filters.source.length) f.source = filters.source.join(',');
    if (dateRange.start_date) f.start_date = dateRange.start_date;
    if (dateRange.end_date) f.end_date = dateRange.end_date;
    return f;
  }, [debouncedSearch, filters, dateRange]);

  const toggleSidebar = () => setIsSidebarOpen(prev => !prev);
  const closeSidebar = () => setIsSidebarOpen(false);

  const renderCheckboxGroup = (title, options, filterKey) => (
    <div className="filter-group" key={filterKey}>
      <h3>{title}</h3>
      {options.map(option => (
        <label key={`${filterKey}-${option.value}`} className="filter-label">
          <input
            type="checkbox"
            className="filter-checkbox"
            checked={filters[filterKey]?.includes(option.value) || false}
            onChange={() => handleFilterChange(filterKey, option.value, !filters[filterKey]?.includes(option.value))}
          />
          <span className="filter-icon-slot">
            {option.icon && <img src={option.icon} className="filter-icon icon" alt="" />}
          </span>
          <span className="filter-label-text">{option.label}</span>
        </label>
      ))}
    </div>
  );

  return (
    <div className="newsfeed-page">
      <Helmet>
        <title>News Feed - Y02 Media Database</title>
      </Helmet>

      <div className="newsfeed-page-hero">
        <h1>News Feed</h1>
        {(periodLinks.weekly || periodLinks.monthly) && (
          <div className="newsfeed-period-links">
            {periodLinks.weekly && (
              <Link to={`/period-summary/${periodLinks.weekly}`} className="btn btn-secondary">
                View Last Week's Summary
              </Link>
            )}
            {periodLinks.monthly && (
              <Link to={`/period-summary/${periodLinks.monthly}`} className="btn btn-secondary">
                View Last Month's Summary
              </Link>
            )}
          </div>
        )}
      </div>

      <div className="media-container newsfeed-page-body">
        <div className={`sidebar-tile ${isSidebarOpen ? 'active' : ''}`} id="sidebar-tile">
          <div className="sidebar-content">
            <div className="sort-search-container">
              <input
                type="text"
                id="search-bar-filter"
                className="input-field desktop-only"
                placeholder="Search updates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="filter-container">
              {renderCheckboxGroup('Importance', IMPORTANCE_OPTIONS, 'importance')}
              {renderCheckboxGroup('Category', CATEGORY_OPTIONS, 'category')}
              {renderCheckboxGroup('Source', SOURCE_OPTIONS, 'source')}

              <div className="filter-group">
                <h3>Date Range</h3>
                <div className="newsfeed-date-range">
                  <label className="newsfeed-date-label">
                    From
                    <input
                      type="date"
                      name="start_date"
                      className="input-field"
                      value={dateRange.start_date}
                      onChange={handleDateChange}
                    />
                  </label>
                  <label className="newsfeed-date-label">
                    To
                    <input
                      type="date"
                      name="end_date"
                      className="input-field"
                      value={dateRange.end_date}
                      onChange={handleDateChange}
                    />
                  </label>
                </div>
              </div>
            </div>

            <button type="button" className="btn btn-secondary newsfeed-clear-btn" onClick={clearFilters} disabled={activeFilterCount === 0}>
              Clear Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </button>
          </div>
        </div>

        <div
          className={`sidebar-overlay ${isSidebarOpen ? 'active' : ''}`}
          onClick={closeSidebar}
        ></div>

        <div id="media-list">
          <div className="sticky-search">
            <input
              type="text"
              id="search-bar-filter-mobile"
              className="input-field mobile-search"
              placeholder="Search updates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button type="button" className="sidebar-toggle-btn" onClick={toggleSidebar}>
              <img src="/static/icons/sliders-solid.svg" className="icon" style={{ height: '20px', width: '20px' }} alt="Toggle Filters" />
            </button>
          </div>

          <NewsFeed apiBaseUrl={apiBaseUrl} filters={feedFilters} />
        </div>
      </div>
    </div>
  );
};

export default NewsFeedPage;